// src\services\axiosInstance.ts
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import type { Dispatch, UnknownAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { API_URL } from '@/Config';
import { incrementActiveRequests, decrementActiveRequests } from '@/stores/loadingSlice';
import { showError } from '@/utils/mySwal';

type SoftswitchRequestConfig = InternalAxiosRequestConfig & {
    suppressAuthRedirect?: boolean;
    skipGlobalLoader?: boolean;
    suppressErrorDialog?: boolean;
    _globalLoaderTracked?: boolean;
    _suppressErrorDialog?: boolean;
};

const SAFE_LOCAL_METHODS = new Set(['get', 'head', 'options']);

const axiosInstance = axios.create({
    baseURL: API_URL,
    timeout: 1000 * 60,
});

type AxiosStoreBindings = {
    dispatch: Dispatch<UnknownAction>;
    createLogoutAction: () => UnknownAction;
};

let storeBindings: AxiosStoreBindings | null = null;

export const configureAxiosStore = (
    dispatch: Dispatch<UnknownAction>,
    createLogoutAction: () => UnknownAction,
) => {
    storeBindings = { dispatch, createLogoutAction };
};

let isAlreadyLoggedOut = false; // Prevent multiple logout flows/toasts

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
    if (!value || typeof value !== 'object') return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
};

const extractSkipGlobalLoader = (config: SoftswitchRequestConfig) => {
    let shouldSkip = config.skipGlobalLoader === true;

    if (isPlainObject(config.params) && 'skipGlobalLoader' in config.params) {
        const { skipGlobalLoader, ...params } = config.params;
        shouldSkip = shouldSkip || skipGlobalLoader === true;
        config.params = params;
    }

    if (isPlainObject(config.data) && 'skipGlobalLoader' in config.data) {
        const { skipGlobalLoader, ...data } = config.data;
        shouldSkip = shouldSkip || skipGlobalLoader === true;
        config.data = data;
    }

    return shouldSkip;
};

const usesLocalLoading = (config: SoftswitchRequestConfig) => {
    const explicitlySkipped = extractSkipGlobalLoader(config);
    const method = (config.method ?? 'get').toLowerCase();
    return explicitlySkipped || SAFE_LOCAL_METHODS.has(method);
};

/**
 * Lets a caller opt out of the global error dialog for a request whose failure it
 * handles itself - an optional or not-yet-deployed endpoint, for example. Like
 * `skipGlobalLoader`, the flag may ride along in `params`/`data` and is stripped
 * before the request leaves.
 */
const extractSuppressErrorDialog = (config: SoftswitchRequestConfig) => {
    let shouldSuppress = config.suppressErrorDialog === true;

    if (isPlainObject(config.params) && 'suppressErrorDialog' in config.params) {
        const { suppressErrorDialog, ...params } = config.params;
        shouldSuppress = shouldSuppress || suppressErrorDialog === true;
        config.params = params;
    }

    if (isPlainObject(config.data) && 'suppressErrorDialog' in config.data) {
        const { suppressErrorDialog, ...data } = config.data;
        shouldSuppress = shouldSuppress || suppressErrorDialog === true;
        config.data = data;
    }

    return shouldSuppress;
};

axiosInstance.interceptors.request.use((config: SoftswitchRequestConfig) => {
    config._globalLoaderTracked = !usesLocalLoading(config);
    config._suppressErrorDialog = extractSuppressErrorDialog(config);

    if (config._globalLoaderTracked) {
        storeBindings?.dispatch(incrementActiveRequests());
    }

    return config
});

axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
        const requestConfig = (response.config || {}) as SoftswitchRequestConfig;
        if (requestConfig._globalLoaderTracked) {
            storeBindings?.dispatch(decrementActiveRequests());
        }

        const cfg = requestConfig as InternalAxiosRequestConfig;
        // const method = cfg.method?.toLowerCase();
        const method = (cfg?.method ?? 'get').toLowerCase();
        const shouldShowToast = !cfg.hideToast && method !== 'get';

        // By default: show success toast if server returned a message
        if (shouldShowToast && response.data?.message) {
            toast.success(response.data.message, { toastId: 'toast-top-right-success' });
        }

        return response;
    },
    (error: AxiosError) => {
        const requestConfig = (error.config || {}) as SoftswitchRequestConfig;
        if (requestConfig._globalLoaderTracked) {
            storeBindings?.dispatch(decrementActiveRequests());
        }

        // AbortController cancellations are an expected lifecycle event, not an
        // API or connectivity failure. Keep rejecting so callers can settle,
        // but never surface an error dialog for an intentional cancellation.
        if (error.code === AxiosError.ERR_CANCELED) {
            return Promise.reject(error);
        }

        if (error.response) {
            const status = error.response.status;
            const { message } = error.response.data as any;
            const cfg = requestConfig;

            const responseData = error.response.data as any;
            const isSessionLimitReached = status === 409 && responseData?.data?.code === "SESSION_LIMIT_REACHED";

            if (status === 401) {
                if (cfg?.suppressAuthRedirect) {
                    if (message) toast.error(message, { toastId: 'toast-top-right-error' });
                } else if (!isAlreadyLoggedOut) {
                    isAlreadyLoggedOut = true;
                    if (storeBindings) {
                        storeBindings.dispatch(storeBindings.createLogoutAction());
                    }
                    // Reset flag so later 401s can trigger again after a delay
                    setTimeout(() => (isAlreadyLoggedOut = false), 3000);

                    if (message) toast.error(message, { toastId: 'toast-top-right-error' });
                    window.location.replace('/');
                }
            } else if (!isSessionLimitReached && !cfg._suppressErrorDialog) {
                if (message) {
                    showError(message);
                }
            }
        } else if (error.request) {
            // toast.error('No response from server. Please check your network.', { toastId: 'toast-top-right-err-first' });
            if (!requestConfig._suppressErrorDialog) {
                showError('No response from server. Please check your network');
            }
        } else if (!requestConfig._suppressErrorDialog) {
            showError(error?.message);
        }

        return Promise.reject(error);
    }
);

export default axiosInstance;


/* 
// GET: no success toast (default)
axiosInstance.get('/users');

// GET but you still don't want success toast (redundant, but valid)
axiosInstance.get('/users', { hideToast: true });

// POST: will show success toast if server returns { message: '...' }
axiosInstance.post('/users', payload);

// POST but suppress success toast
axiosInstance.post('/users', payload, { hideToast: true }); 
*/
